import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdCalendarToday, MdRepeat, MdTimelapse } from 'react-icons/md';
import { useQueryClient } from '@tanstack/react-query';
import { treatmentExerciseService } from 'services/treatmentExerciseService';
import { useTreatmentExercises } from 'hooks/useTreatmentExercises';
import { EstadoPlan, Patient, TreatmentExercise, TreatmentPlan } from 'types/models';
import { generatePlanPdf, formatPhoneForWhatsApp, PdfExercise } from 'features/treatments/utils/generatePlanPdf';
import SessionsPanel from './SessionsPanel';

const ActivatePlanWizard = lazy(() => import('./ActivatePlanWizard'));

type ActiveTab = 'exercises' | 'sessions';

// ─── Catalog ──────────────────────────────────────────────────────────────────

interface CatalogItem { id: string; name: string; bodyPart: string; category: string; }

const CATALOG: CatalogItem[] = [
  { id: 'cat-001', name: 'Ejercicio de Codman',            bodyPart: 'Hombro',  category: 'Movilidad'       },
  { id: 'cat-002', name: 'Elevación frontal de hombro',    bodyPart: 'Hombro',  category: 'Fortalecimiento' },
  { id: 'cat-003', name: 'Rotación externa hombro',        bodyPart: 'Hombro',  category: 'Fortalecimiento' },
  { id: 'cat-004', name: 'Estiramiento de flexores',       bodyPart: 'Muñeca',  category: 'Estiramiento'    },
  { id: 'cat-005', name: 'Extensión de cuádriceps',        bodyPart: 'Rodilla', category: 'Fortalecimiento' },
  { id: 'cat-006', name: 'Flexión de rodilla activa',      bodyPart: 'Rodilla', category: 'Movilidad'       },
  { id: 'cat-007', name: 'Sentadilla asistida',            bodyPart: 'Rodilla', category: 'Fortalecimiento' },
  { id: 'cat-008', name: 'Puente glúteo',                  bodyPart: 'Lumbar',  category: 'Fortalecimiento' },
  { id: 'cat-009', name: 'Estiramiento Gato-Camello',      bodyPart: 'Lumbar',  category: 'Estiramiento'    },
  { id: 'cat-010', name: 'Bird-Dog',                       bodyPart: 'Lumbar',  category: 'Estabilidad'     },
  { id: 'cat-011', name: 'Pelvic tilt',                    bodyPart: 'Lumbar',  category: 'Movilidad'       },
  { id: 'cat-012', name: 'Retracción cervical',            bodyPart: 'Cuello',  category: 'Estabilidad'     },
  { id: 'cat-013', name: 'Flexión cervical activa',        bodyPart: 'Cuello',  category: 'Movilidad'       },
  { id: 'cat-014', name: 'Dorsiflexión activa de tobillo', bodyPart: 'Tobillo', category: 'Movilidad'       },
  { id: 'cat-015', name: 'Isométrico de pantorrilla',      bodyPart: 'Tobillo', category: 'Fortalecimiento' },
  { id: 'cat-016', name: 'Pronación y supinación muñeca',  bodyPart: 'Muñeca',  category: 'Movilidad'       },
  { id: 'cat-017', name: 'Abducción de cadera',            bodyPart: 'Cadera',  category: 'Fortalecimiento' },
  { id: 'cat-018', name: 'Estiramiento piriforme',         bodyPart: 'Cadera',  category: 'Estiramiento'    },
];

const BODY_COLORS: Record<string, string> = {
  Hombro: 'blue', Muñeca: 'cyan', Rodilla: 'green',
  Lumbar: 'orange', Cuello: 'purple', Tobillo: 'teal', Cadera: 'pink',
};

const ESTADO_CONFIG: Record<EstadoPlan, { colorScheme: string; label: string }> = {
  [EstadoPlan.BORRADOR]:   { colorScheme: 'orange', label: 'Borrador'   },
  [EstadoPlan.ACTIVO]:     { colorScheme: 'brand',  label: 'Activo'     },
  [EstadoPlan.COMPLETADO]: { colorScheme: 'green',  label: 'Completado' },
  [EstadoPlan.CANCELADO]:  { colorScheme: 'gray',   label: 'Cancelado'  },
};

// ─── Local exercise state ─────────────────────────────────────────────────────

type VolumeUnit = 'reps' | 'seconds';

interface LocalEx {
  _key:            string;
  id?:             string;
  name:            string;
  baseExerciseId?: string;
  series:          string;
  volumeValue:     string;
  volumeUnit:      VolumeUnit;
  load:            string;
  restTime:        string;
  notes:           string;
  isNew:           boolean;
  isDirty:         boolean;
  isDeleted:       boolean;
}

const makeKey = () => `k-${Math.random().toString(36).slice(2)}`;

const fromServer = (ex: TreatmentExercise): LocalEx => ({
  _key:        ex.id,
  id:          ex.id,
  name:        ex.name,
  series:      String(ex.dosage?.series ?? 3),
  volumeValue: String(ex.dosage?.repetitions ?? ex.dosage?.executionTime ?? 10),
  volumeUnit:  ex.dosage?.executionTime != null ? 'seconds' : 'reps',
  load:        ex.dosage?.load ?? '',
  restTime:    ex.dosage?.restTime ?? '',
  notes:       ex.dosage?.notes ?? '',
  isNew: false, isDirty: false, isDeleted: false,
});

const toDosage = (e: LocalEx) => ({
  series:       Math.max(1, parseInt(e.series, 10) || 1),
  ...(e.volumeUnit === 'reps'
    ? { repetitions:   Math.max(1, parseInt(e.volumeValue, 10) || 1) }
    : { executionTime: Math.max(1, parseInt(e.volumeValue, 10) || 1) }),
  load:     e.load.trim(),
  restTime: e.restTime.trim() || undefined,
  notes:    e.notes.trim(),
});

function formatFecha(d: string) {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────

function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
    </svg>
  );
}
function IconDrag() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9"  cy="5"  r="1"/><circle cx="9"  cy="12" r="1"/><circle cx="9"  cy="19" r="1"/>
      <circle cx="15" cy="5"  r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="19" r="1"/>
    </svg>
  );
}
function IconPlus() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14"/><path d="M5 12h14"/>
    </svg>
  );
}
function IconWhatsApp() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
    </svg>
  );
}

// ─── CatalogPanel ─────────────────────────────────────────────────────────────

function CatalogPanel({ onAdd }: { onAdd: (item: CatalogItem) => void }) {
  const [search, setSearch] = useState('');

  const labelColor  = useColorModeValue('gray.400',  'gray.500');
  const hoverBg     = useColorModeValue('gray.50',   'rgba(99,102,241,0.06)');
  const nameColor   = useColorModeValue('gray.700',  'gray.200');
  const inputBg     = useColorModeValue('white',     'gray.700');
  const inputBorder = useColorModeValue('gray.200',  'gray.600');
  const addBtnBg    = useColorModeValue('brand.500', 'brand.400');

  const filtered = search.length >= 1
    ? CATALOG.filter(e =>
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.bodyPart.toLowerCase().includes(search.toLowerCase())
      )
    : CATALOG;

  return (
    <Flex direction="column" h="100%" gap="8px">
      <Text fontSize="10px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.1em">
        Catálogo
      </Text>

      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none" color={labelColor} h="100%">
          <IconSearch />
        </InputLeftElement>
        <Input
          pl="32px"
          placeholder="Buscar ejercicio o zona..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          bg={inputBg}
          border="1px solid"
          borderColor={inputBorder}
          borderRadius="6px"
          fontSize="xs"
          _focus={{ borderColor: 'brand.400', boxShadow: 'none' }}
          _placeholder={{ color: labelColor }}
        />
      </InputGroup>

      <Box flex="1" overflowY="auto" pr="2px" mx="-2px">
        <VStack spacing="1px" align="stretch">
          {filtered.map(item => (
            <Flex
              key={item.id}
              px="8px" py="7px"
              borderRadius="6px"
              align="center"
              gap="8px"
              _hover={{ bg: hoverBg }}
              transition="background 0.1s"
              role="group"
              cursor="default"
            >
              <VStack align="flex-start" spacing="1px" flex="1" minW="0">
                <Text fontSize="xs" fontWeight="500" color={nameColor} noOfLines={1}>{item.name}</Text>
                <Flex gap="4px" align="center">
                  <Badge
                    colorScheme={BODY_COLORS[item.bodyPart] ?? 'gray'}
                    variant="subtle"
                    fontSize="9px" px="5px" py="0px" borderRadius="4px"
                  >
                    {item.bodyPart}
                  </Badge>
                  <Text fontSize="9px" color={labelColor}>{item.category}</Text>
                </Flex>
              </VStack>
              <Tooltip label="Añadir al plan" placement="top" hasArrow openDelay={400}>
                <Box
                  as="button"
                  w="22px" h="22px" borderRadius="5px"
                  bg={addBtnBg} color="white"
                  display="flex" alignItems="center" justifyContent="center"
                  flexShrink={0}
                  opacity={0} _groupHover={{ opacity: 1 }}
                  transition="opacity 0.1s"
                  onClick={() => onAdd(item)}
                  _hover={{ opacity: 1 }}
                >
                  <IconPlus />
                </Box>
              </Tooltip>
            </Flex>
          ))}
        </VStack>
      </Box>
    </Flex>
  );
}

// ─── ExerciseRow ──────────────────────────────────────────────────────────────

interface ExerciseRowProps {
  ex:             LocalEx;
  index:          number;
  onChange:       (key: string, field: keyof LocalEx, value: string) => void;
  onDelete:       (key: string) => void;
  onDragStart:    (e: React.DragEvent, key: string) => void;
  onDragOver:     (e: React.DragEvent, key: string) => void;
  onDrop:         (e: React.DragEvent, key: string) => void;
  isDraggingOver: boolean;
}

function ExerciseRow({ ex, index, onChange, onDelete, onDragStart, onDragOver, onDrop, isDraggingOver }: ExerciseRowProps) {
  const borderColor   = useColorModeValue('gray.150', 'gray.700');
  const dragColor     = useColorModeValue('brand.300', 'brand.500');
  const cardBg        = useColorModeValue('white',     'gray.800');
  const labelColor    = useColorModeValue('gray.400',  'gray.500');
  const dragHandleClr = useColorModeValue('gray.300',  'gray.600');
  const inputBg       = useColorModeValue('gray.50',   'gray.750');
  const numColor      = useColorModeValue('brand.500', 'brand.400');
  const divColor      = useColorModeValue('gray.100',  'gray.700');

  return (
    <Box
      draggable
      onDragStart={e => onDragStart(e, ex._key)}
      onDragOver={e => { e.preventDefault(); onDragOver(e, ex._key); }}
      onDrop={e => onDrop(e, ex._key)}
      border="1px solid"
      borderColor={isDraggingOver ? dragColor : borderColor}
      borderRadius="8px"
      bg={cardBg}
      transition="border-color 0.15s"
      overflow="hidden"
    >
      {/* Header row — no background, just a bottom divider */}
      <HStack
        px="10px" py="8px"
        borderBottom="1px solid" borderColor={divColor}
        spacing="8px"
      >
        <Box color={dragHandleClr} cursor="grab" flexShrink={0}><IconDrag /></Box>
        <Text
          fontFamily="mono" fontSize="10px" fontWeight="700"
          color={numColor} flexShrink={0} w="18px" textAlign="center"
        >
          {index + 1}
        </Text>
        <Input
          value={ex.name}
          onChange={e => onChange(ex._key, 'name', e.target.value)}
          variant="unstyled"
          fontSize="sm"
          fontWeight="500"
          flex="1"
          placeholder="Nombre del ejercicio..."
          _placeholder={{ color: labelColor }}
        />
        <Tooltip label="Eliminar" placement="top" hasArrow openDelay={400}>
          <Box
            as="button"
            color={labelColor}
            _hover={{ color: 'red.400' }}
            transition="color 0.1s"
            onClick={() => onDelete(ex._key)}
            flexShrink={0}
          >
            <IconTrash />
          </Box>
        </Tooltip>
      </HStack>

      {/* Dosage grid */}
      <Flex px="10px" py="8px" gap="8px" wrap="wrap" align="flex-start">
        {/* Series */}
        <Box minW="72px">
          <Text fontSize="9px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="3px">Series</Text>
          <NumberInput value={ex.series} min={1} max={20} onChange={v => onChange(ex._key, 'series', v)} size="sm">
            <NumberInputField bg={inputBg} borderRadius="6px" fontSize="xs" />
            <NumberInputStepper>
              <NumberIncrementStepper /><NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </Box>

        {/* Reps / Tiempo */}
        <Box minW="140px" flex="1">
          <Text fontSize="9px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="3px">
            {ex.volumeUnit === 'reps' ? 'Repeticiones' : 'Tiempo (seg)'}
          </Text>
          <HStack spacing="5px">
            <NumberInput value={ex.volumeValue} min={1} max={ex.volumeUnit === 'reps' ? 200 : 3600} onChange={v => onChange(ex._key, 'volumeValue', v)} size="sm" flex="1">
              <NumberInputField bg={inputBg} borderRadius="6px" fontSize="xs" />
              <NumberInputStepper>
                <NumberIncrementStepper /><NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Select value={ex.volumeUnit} onChange={e => onChange(ex._key, 'volumeUnit', e.target.value)} bg={inputBg} borderRadius="6px" fontSize="xs" w="68px" flexShrink={0} size="sm">
              <option value="reps">Reps</option>
              <option value="seconds">Seg</option>
            </Select>
          </HStack>
        </Box>

        {/* Carga */}
        <Box minW="140px" flex="2">
          <Text fontSize="9px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="3px">Carga</Text>
          <Input value={ex.load} onChange={e => onChange(ex._key, 'load', e.target.value)} placeholder="Sin carga / libre" bg={inputBg} borderRadius="6px" fontSize="xs" size="sm" _placeholder={{ color: labelColor }} />
        </Box>

        {/* Notas */}
        <Box w="100%">
          <Text fontSize="9px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="3px">Notas técnicas</Text>
          <Textarea value={ex.notes} onChange={e => onChange(ex._key, 'notes', e.target.value)} placeholder="Indicaciones técnicas para el paciente..." bg={inputBg} borderRadius="6px" fontSize="xs" rows={2} resize="none" size="sm" _placeholder={{ color: labelColor }} />
        </Box>
      </Flex>
    </Box>
  );
}

// ─── TreatmentCard ────────────────────────────────────────────────────────────

interface Props {
  plan:    TreatmentPlan;
  patient: Patient;
}

export default function TreatmentCard({ plan, patient }: Props) {
  const toast       = useToast();
  const queryClient = useQueryClient();

  const [exercises, setExercises]   = useState<LocalEx[]>([]);
  const [isSaving, setIsSaving]     = useState(false);
  const [isSharing, setIsSharing]   = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeTab, setActiveTab]   = useState<ActiveTab>('exercises');
  const dragKeyRef                  = useRef<string | null>(null);
  const [overKey, setOverKey]       = useState<string | null>(null);

  const isDraft = plan.estado === EstadoPlan.BORRADOR || (plan.status as any) === EstadoPlan.BORRADOR;

  const { data: serverExercises } = useTreatmentExercises(plan.id);

  useEffect(() => {
    if (serverExercises) setExercises(serverExercises.map(fromServer));
  }, [serverExercises]);

  // ── Colors ───────────────────────────────────────────────────────────────────
  const cardBg        = useColorModeValue('white',      'navy.800');
  const borderColor   = useColorModeValue('gray.150',   'whiteAlpha.100');
  const labelColor    = useColorModeValue('gray.400',   'gray.500');
  const divColor      = useColorModeValue('gray.100',   'gray.700');
  const titleColor    = useColorModeValue('gray.800',   'white');
  const progressTrack = useColorModeValue('gray.100',   'navy.700');
  const emptyBg       = useColorModeValue('gray.50',    'gray.800');
  const emptyBorder   = useColorModeValue('gray.150',   'gray.700');
  const tabSelectorBg = useColorModeValue('gray.100',   'gray.750');
  const activeTabBg   = useColorModeValue('white',      'gray.600');
  const activeTabClr  = useColorModeValue('gray.800',   'white');
  const metaColor     = useColorModeValue('gray.400',   'gray.500');

  const cfg     = ESTADO_CONFIG[plan.estado] ?? { colorScheme: 'gray', label: plan.estado ?? '—' };
  const visible = exercises.filter(e => !e.isDeleted);

  // ── Catalog add ──────────────────────────────────────────────────────────────
  const handleAddFromCatalog = (item: CatalogItem) => {
    setExercises(prev => [...prev, {
      _key: makeKey(), name: item.name,
      series: '3', volumeValue: '10', volumeUnit: 'reps',
      load: '', restTime: '', notes: '',
      isNew: true, isDirty: false, isDeleted: false,
    }]);
  };

  // ── Field change ─────────────────────────────────────────────────────────────
  const handleChange = (key: string, field: keyof LocalEx, value: string) => {
    setExercises(prev => prev.map(e =>
      e._key === key ? { ...e, [field]: value, isDirty: !e.isNew } : e
    ));
  };

  // ── Delete ────────────────────────────────────────────────────────────────────
  const handleDelete = (key: string) => {
    setExercises(prev => prev.map(e => e._key === key ? { ...e, isDeleted: true } : e));
  };

  // ── Drag & Drop ───────────────────────────────────────────────────────────────
  const handleDragStart = (_e: React.DragEvent, key: string) => { dragKeyRef.current = key; };
  const handleDragOver  = (_e: React.DragEvent, key: string) => { setOverKey(key); };
  const handleDrop      = (_e: React.DragEvent, targetKey: string) => {
    const fromKey = dragKeyRef.current;
    if (!fromKey || fromKey === targetKey) { setOverKey(null); return; }
    const list = [...exercises];
    const fi   = list.findIndex(e => e._key === fromKey);
    const ti   = list.findIndex(e => e._key === targetKey);
    if (fi < 0 || ti < 0) { setOverKey(null); return; }
    const [item] = list.splice(fi, 1);
    list.splice(ti, 0, item);
    setExercises(list);
    dragKeyRef.current = null;
    setOverKey(null);
  };

  // ── Validate ──────────────────────────────────────────────────────────────────
  const validate = () => {
    const active = exercises.filter(e => !e.isDeleted);
    for (const ex of active) {
      if (!ex.name.trim()) {
        toast({ title: 'Todos los ejercicios deben tener nombre.', status: 'warning', duration: 3000, isClosable: true, position: 'top-right' });
        return false;
      }
      if (!ex.load.trim()) {
        toast({ title: `"${ex.name}": indica la carga o resistencia.`, status: 'warning', duration: 3000, isClosable: true, position: 'top-right' });
        return false;
      }
      if (!ex.notes.trim()) {
        toast({ title: `"${ex.name}": agrega indicaciones técnicas.`, status: 'warning', duration: 3000, isClosable: true, position: 'top-right' });
        return false;
      }
    }
    return true;
  };

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      for (const ex of exercises.filter(e => e.isDeleted && !e.isNew && e.id)) {
        await treatmentExerciseService.remove(plan.id, ex.id!);
      }
      for (const ex of exercises.filter(e => e.isNew && !e.isDeleted)) {
        await treatmentExerciseService.prescribe(plan.id, {
          baseExerciseId: ex.baseExerciseId,
          name:           ex.name.trim(),
          dosage:         toDosage(ex),
        });
      }
      for (const ex of exercises.filter(e => !e.isNew && !e.isDeleted && e.isDirty && e.id)) {
        await treatmentExerciseService.update(plan.id, ex.id!, {
          name:   ex.name.trim(),
          dosage: toDosage(ex),
        });
      }
      queryClient.invalidateQueries({ queryKey: ['treatment-exercises', plan.id] });
      toast({ title: 'Plan guardado.', status: 'success', duration: 2500, isClosable: true, position: 'top-right' });
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Error al guardar.';
      toast({
        title:       'Error',
        description: Array.isArray(msg) ? msg.join(' · ') : msg,
        status:      'error', duration: 5000, isClosable: true, position: 'top-right',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Share WhatsApp ─────────────────────────────────────────────────────────────
  const handleShareWhatsApp = () => {
    const active = exercises.filter(e => !e.isDeleted);
    if (active.length === 0) {
      toast({ title: 'Sin ejercicios en la rutina', description: 'Agrega al menos un ejercicio antes de compartir.', status: 'warning', duration: 3000, isClosable: true, position: 'top-right' });
      return;
    }
    const phone = formatPhoneForWhatsApp(patient.telefono);
    if (!phone) {
      toast({ title: 'Sin número de teléfono', description: 'El paciente no tiene teléfono registrado.', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
      return;
    }

    setIsSharing(true);
    try {
      const pdfExercises: PdfExercise[] = active.map(e => ({
        name:        e.name,
        series:      e.series,
        volumeValue: e.volumeValue,
        volumeUnit:  e.volumeUnit,
        load:        e.load,
        notes:       e.notes,
      }));

      const blob = generatePlanPdf({
        patientName:  `${patient.nombres} ${patient.apellidos}`,
        planNumber:   plan.numeroPlan,
        objective:    plan.objetivoTerapeutico,
        fechaInicio:  plan.fechaInicio,
        exercises:    pdfExercises,
      });

      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `rutina-plan-${plan.numeroPlan}-${patient.apellidos.toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      const nombre = patient.nombres.split(' ')[0];
      const msg    = encodeURIComponent(
        `Hola ${nombre} 👋, te enviamos la rutina de ejercicios de tu *Plan #${plan.numeroPlan}* de FisioLab.\n\nAcabas de recibir el PDF con todos los ejercicios, series, cargas e indicaciones. ¡Cualquier duda estamos a tu disposición! 💪`
      );
      window.open(`https://wa.me/${phone}?text=${msg}`, '_blank', 'noopener,noreferrer');

      toast({ title: 'PDF descargado y WhatsApp abierto', status: 'success', duration: 3000, isClosable: true, position: 'top-right' });
    } catch {
      toast({ title: 'Error al generar el PDF', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="14px"
      overflow="hidden"
    >
      {/* 2px progress accent — top of card */}
      <Box h="2px" bg={progressTrack}>
        <Box
          h="100%"
          w={`${plan.progresoPorcentaje ?? 0}%`}
          bg="brand.500"
          transition="width 0.4s ease"
        />
      </Box>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Flex
        px="20px" py="14px"
        borderBottom="1px solid" borderColor={divColor}
        align="center" gap="10px" flexWrap="wrap"
      >
        {/* Plan ID + status + title */}
        <Flex align="center" gap="8px" flex="1" minW="0" flexWrap="wrap">
          <Text fontFamily="mono" fontSize="xs" fontWeight="700" color="brand.500" flexShrink={0}>
            #{plan.numeroPlan}
          </Text>
          <Badge
            colorScheme={cfg.colorScheme}
            variant="subtle"
            borderRadius="full"
            px="7px" py="1px"
            fontSize="9px" fontWeight="700"
            flexShrink={0}
          >
            {cfg.label}
          </Badge>
          <Box w="3px" h="3px" borderRadius="full" bg={divColor} flexShrink={0} />
          <Text fontSize="sm" fontWeight="600" color={titleColor} noOfLines={1} flex="1" minW="0">
            {plan.objetivoTerapeutico}
          </Text>
          <Text fontSize="xs" fontWeight="600" color={labelColor} flexShrink={0}>
            {plan.progresoPorcentaje ?? 0}%
          </Text>
        </Flex>

        {/* Tab toggle */}
        {!isDraft && (
          <HStack spacing="2px" bg={tabSelectorBg} borderRadius="8px" p="2px" flexShrink={0}>
            {(['exercises', 'sessions'] as ActiveTab[]).map(tab => (
              <Box
                key={tab}
                as="button"
                px="10px" py="4px"
                borderRadius="6px"
                fontSize="xs" fontWeight="500"
                bg={activeTab === tab ? activeTabBg : 'transparent'}
                color={activeTab === tab ? activeTabClr : labelColor}
                boxShadow={activeTab === tab ? 'sm' : 'none'}
                transition="all 0.12s"
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'exercises' ? 'Rutina' : 'Sesiones'}
              </Box>
            ))}
          </HStack>
        )}

        {/* Actions */}
        <HStack spacing="6px" flexShrink={0}>
          {isDraft && (
            <Button
              size="sm"
              colorScheme="green"
              variant="solid"
              borderRadius="7px"
              onClick={() => setWizardOpen(true)}
              leftIcon={
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
              }
            >
              Activar
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            color="whatsapp.500"
            _hover={{ bg: 'whatsapp.50', _dark: { bg: 'rgba(37,211,102,0.08)' } }}
            leftIcon={<Box as="span" display="flex"><IconWhatsApp /></Box>}
            onClick={handleShareWhatsApp}
            isLoading={isSharing}
            loadingText="PDF..."
          >
            Compartir
          </Button>
          <Button
            size="sm"
            colorScheme="brand"
            borderRadius="7px"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Guardando..."
          >
            Guardar
          </Button>
        </HStack>
      </Flex>

      {/* Meta info row — dates, frequency, duration */}
      {(plan.fechaInicio || plan.frecuenciaSemanal || plan.duracionEstimadaSemanas) && (
        <Flex
          px="20px" py="6px"
          gap="16px" flexWrap="wrap"
          borderBottom="1px solid" borderColor={divColor}
        >
          {plan.fechaInicio && (
            <HStack spacing="4px" color={metaColor}>
              <Icon as={MdCalendarToday} w="10px" h="10px" />
              <Text fontSize="xs">
                {formatFecha(plan.fechaInicio)}
                {plan.fechaFin ? ` → ${formatFecha(plan.fechaFin)}` : ''}
              </Text>
            </HStack>
          )}
          {plan.frecuenciaSemanal && (
            <HStack spacing="4px" color={metaColor}>
              <Icon as={MdRepeat} w="10px" h="10px" />
              <Text fontSize="xs">{plan.frecuenciaSemanal}×/sem</Text>
            </HStack>
          )}
          {plan.duracionEstimadaSemanas && (
            <HStack spacing="4px" color={metaColor}>
              <Icon as={MdTimelapse} w="10px" h="10px" />
              <Text fontSize="xs">{plan.duracionEstimadaSemanas} sem</Text>
            </HStack>
          )}
        </Flex>
      )}

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      <Flex h="520px" overflow="hidden">

        {/* Left: Catalog */}
        <Box
          w="260px"
          flexShrink={0}
          borderRight="1px solid"
          borderColor={divColor}
          px="12px"
          py="12px"
          overflowY="auto"
          display="flex"
          flexDirection="column"
        >
          <CatalogPanel onAdd={handleAddFromCatalog} />
        </Box>

        {/* Activate Plan Wizard */}
        {isDraft && (
          <Suspense fallback={null}>
            <ActivatePlanWizard
              planId={plan.id}
              totalSessions={plan.totalSessions ?? 10}
              isOpen={wizardOpen}
              onClose={() => setWizardOpen(false)}
              onActivated={() => {
                setWizardOpen(false);
                queryClient.invalidateQueries({ queryKey: ['plans'] });
              }}
            />
          </Suspense>
        )}

        {/* Sessions panel */}
        {!isDraft && activeTab === 'sessions' && plan.pacienteId && (
          <Box flex="1" px="16px" py="12px" overflowY="auto" display="flex" flexDirection="column">
            <SessionsPanel
              patientId={plan.pacienteId}
              episodeId={plan.episodeId}
              planId={plan.id}
            />
          </Box>
        )}

        {/* Exercises panel */}
        <Box
          flex="1"
          px="16px"
          py="12px"
          overflowY="auto"
          display={!isDraft && activeTab === 'sessions' ? 'none' : 'flex'}
          flexDirection="column"
          gap="10px"
        >
          {/* Section header */}
          <HStack justify="space-between" mb="2px">
            <HStack spacing="6px">
              <Text fontSize="10px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.1em">
                Rutina
              </Text>
              {visible.length > 0 && (
                <Text fontFamily="mono" fontSize="10px" fontWeight="700" color={labelColor}>
                  {visible.length}
                </Text>
              )}
            </HStack>
            <Text fontSize="10px" color={labelColor}>Arrastra para reordenar</Text>
          </HStack>

          {visible.length === 0 ? (
            <Flex
              direction="column" align="center" justify="center" flex="1"
              bg={emptyBg} borderRadius="10px"
              border="1px dashed" borderColor={emptyBorder}
              gap="8px" py="40px"
            >
              <Text fontSize="xs" color={labelColor} textAlign="center" maxW="220px">
                Haz clic en [+] del catálogo para agregar ejercicios
              </Text>
            </Flex>
          ) : (
            <VStack spacing="8px" align="stretch">
              {visible.map((ex, idx) => (
                <ExerciseRow
                  key={ex._key}
                  ex={ex}
                  index={idx}
                  onChange={handleChange}
                  onDelete={handleDelete}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  isDraggingOver={overKey === ex._key}
                />
              ))}
            </VStack>
          )}
        </Box>
      </Flex>
    </Box>
  );
}
